/**
 * VIB-1283 (lønnsdelen): bygger statistlista for CSV-lønnseksport.
 *
 * Datamodell-avklaring (verifisert i supabase/migrations/000_baseline.sql +
 * src/hooks/use-project-extras.ts): statister timeføres IKKE — det finnes
 * ingen kobling til time_entries og ingen timesats-felt på project_extras.
 * Betalingsgrunnlaget er et flatt avtalt beløp (`agreed_amount`) per person,
 * jf. bølge 8-spec (docs/featureliste-2026-07/bolger/bolge-08-kontrakter-statister.md,
 * VIB-1283-seksjonen): "Statistliste (personnr, kontonr, beløp per person med
 * payment_type='lonnet')". Kolonnesettet i denne modulen bruker derfor
 * agreed_amount direkte — IKKE timer × sats (den kolonnen finnes ikke i
 * datamodellen, se PR-beskrivelsen for detaljer).
 *
 * Filtrering (Erik 21.07, presisert 21.07): kun EKSPLISITT bekreftede
 * lønnsmottakere skal med — payment_type='lonnet' (case-/whitespace-
 * uavhengig). Opprinnelig instruks var å ekskludere kun 'faktura', men Erik
 * strammet inn: tom/NULL/ukjent payment_type skal også EKSKLUDERES, så ingen
 * statist med uavklart status havner i lønnsfila med personnr/kontonr.
 * `payment_type` er en legacy-kolonne (VIB-572-kommentar: "erstattet av
 * budget_line_id") som i dag kun fylles av sync-monday-extras fra en
 * fritekst-statuskolonne på Monday-siden — derfor sammenlignes case-/
 * whitespace-uavhengig i stedet for en streng enum-sjekk.
 */

/** Rå rad fra project_extras (+ embedded projects-join), filtrert på deleted_at IS NULL i spørringen. */
export interface RawExtraSalaryRow {
  id: string;
  project_id: string | null;
  full_name: string | null;
  /** Legacy-felt (pre VIB-675/1387) — brukes kun som fallback hvis full_name mangler. */
  name: string | null;
  personal_number: string | null;
  bank_account: string | null;
  agreed_amount: number | null;
  payment_type: string | null;
  projects: { name: string | null; project_code: string | null } | null;
}

export interface ExtrasSalaryEntry {
  extra_id: string;
  full_name: string;
  personal_number: string | null;
  bank_account: string | null;
  project_id: string | null;
  project_code: string | null;
  project_name: string;
  agreed_amount: number;
}

/**
 * Sant kun hvis personen er en EKSPLISITT bekreftet lønnsmottaker
 * (payment_type='lonnet'). Case-/whitespace-uavhengig fordi payment_type
 * kommer fra en Monday-statuskolonne (fritekst-label, se
 * supabase/functions/sync-monday-extras). Tom streng, NULL og alle andre
 * verdier (inkl. "faktura") gir false — uavklart status skal IKKE havne i
 * lønnsfila med personnr/kontonr (Erik 21.07, presisert samme dag).
 */
export function isSalaryPaymentType(paymentType: string | null | undefined): boolean {
  return (paymentType ?? "").trim().toLowerCase() === "lonnet";
}

/**
 * Bygger lønns-CSV-grunnlaget fra rå project_extras-rader: filtrerer til KUN
 * eksplisitt bekreftede lønnsmottakere (payment_type='lonnet') og rader uten
 * prosjekttilknytning (statister uten project_id kan ikke plasseres i noe
 * prosjekt-regnskap), og normaliserer navn/prosjektfelter for visning.
 */
export function buildExtrasSalaryExport(rows: RawExtraSalaryRow[]): ExtrasSalaryEntry[] {
  return rows
    .filter((r) => isSalaryPaymentType(r.payment_type))
    .filter((r) => r.project_id !== null)
    .map((r) => ({
      extra_id: r.id,
      full_name: r.full_name ?? r.name ?? "(uten navn)",
      personal_number: r.personal_number,
      bank_account: r.bank_account,
      project_id: r.project_id,
      project_code: r.projects?.project_code ?? null,
      project_name: r.projects?.name ?? "(ukjent prosjekt)",
      agreed_amount: Math.round(r.agreed_amount ?? 0),
    }));
}
